// 문자 발송 관리 페이지
import React, { useState } from "react";
import {
  Flex,
  Grid,
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Text,
  Badge,
  VStack,
  HStack,
  Divider,
  Code,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";

function SMSManagement() {
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const bgColor = useColorModeValue("white", "gray.700");

  // API 서버 URL
  const NOTIFICATION_SERVER = process.env.REACT_APP_NOTIFICATION_SERVER || "http://localhost:3001";
  
  // 발송번호 (환경변수에 설정됨)
  const FROM_NUMBER = "051-510-3750";

  // 문자 발송 폼 상태
  const [smsForm, setSmsForm] = useState({
    phoneNumber: "",
    content: "",
    messageType: "AUTO",
    name: "",
    subject: "",
  });

  // 수신자 목록 상태
  const [recipients, setRecipients] = useState([]);
  const [tempRecipient, setTempRecipient] = useState({
    phoneNumber: "",
    name: "",
  });

  // 로딩 상태
  const [loading, setLoading] = useState(false);

  // 응답 결과
  const [response, setResponse] = useState(null);

  // 글자 수 계산
  const contentLength = smsForm.content.length;
  const contentBytes = new Blob([smsForm.content]).size;
  const estimatedType = contentBytes <= 90 ? "SMS" : "LMS";

  /**
   * 수신자 추가
   */
  const handleAddRecipient = () => {
    if (!tempRecipient.phoneNumber) {
      toast({
        title: "입력 오류",
        description: "전화번호를 입력해주세요.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // 중복 확인
    const isDuplicate = recipients.some(
      (r) => r.phoneNumber === tempRecipient.phoneNumber
    );
    
    if (isDuplicate) {
      toast({
        title: "중복된 번호",
        description: "이미 추가된 전화번호입니다.",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setRecipients([
      ...recipients,
      {
        id: Date.now(),
        phoneNumber: tempRecipient.phoneNumber,
        name: tempRecipient.name || "수신자",
      },
    ]);

    setTempRecipient({ phoneNumber: "", name: "" });
  };

  /**
   * 수신자 개별 삭제
   */
  const handleRemoveRecipient = (id) => {
    setRecipients(recipients.filter((r) => r.id !== id));
  };

  /**
   * 수신자 전체 삭제
   */
  const handleClearRecipients = () => {
    if (recipients.length === 0) return;
    
    if (window.confirm(`${recipients.length}명의 수신자를 모두 삭제하시겠습니까?`)) {
      setRecipients([]);
    }
  };

  /**
   * 문자(SMS/LMS) 발송
   */
  const handleSendSMS = async () => {
    // 유효성 검사
    if (recipients.length === 0) {
      toast({
        title: "발송 대상 없음",
        description: "수신자를 추가해주세요.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!smsForm.content) {
      toast({
        title: "입력 오류",
        description: "메시지 내용을 입력해주세요.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setResponse(null);

    try {
      let successCount = 0;
      let failCount = 0;
      const results = [];

      // 각 수신자에게 발송
      for (const recipient of recipients) {
        try {
          const res = await fetch(`${NOTIFICATION_SERVER}/api/test/send-sms`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              phoneNumber: recipient.phoneNumber,
              content: smsForm.content,
              messageType: smsForm.messageType,
              name: recipient.name,
              subject: smsForm.subject || "알림",
            }),
          });

          const data = await res.json();

          if (data.success) {
            successCount++;
            results.push({
              recipient: recipient.name,
              phoneNumber: recipient.phoneNumber,
              success: true,
            });
          } else {
            failCount++;
            results.push({
              recipient: recipient.name,
              phoneNumber: recipient.phoneNumber,
              success: false,
              error: data.error,
            });
          }
        } catch (error) {
          failCount++;
          results.push({
            recipient: recipient.name,
            phoneNumber: recipient.phoneNumber,
            success: false,
            error: error.message,
          });
        }
      }

      toast({
        title: "문자 발송 완료",
        description: `성공: ${successCount}건, 실패: ${failCount}건`,
        status: successCount > 0 ? "success" : "error",
        duration: 5000,
        isClosable: true,
      });

      setResponse({
        success: true,
        totalCount: recipients.length,
        successCount,
        failCount,
        results,
      });

      // 발송 성공 시 수신자 목록 초기화
      if (successCount > 0) {
        setRecipients([]);
      }
    } catch (error) {
      toast({
        title: "문자 발송 실패",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setResponse({ success: false, error: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Grid
        templateColumns={{ sm: "1fr", lg: "3fr 2fr" }}
        gap="20px"
      >
        {/* 좌측: 문자 발송 폼 */}
        <Box>
          <Card>
            <CardHeader mb="20px">
              <Text fontSize="lg" color={textColor} fontWeight="bold">
                문자 발송
              </Text>
              <Text fontSize="sm" color="gray.500" mt={2} ml={2}>
                SMS(90바이트 이하) 또는 LMS(90바이트 초과) 자동 선택
              </Text>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold" color={textColor}>
                    메시지 타입
                  </FormLabel>
                  <Select
                    value={smsForm.messageType}
                    onChange={(e) =>
                      setSmsForm({ ...smsForm, messageType: e.target.value })
                    }
                  >
                    <option value="AUTO">자동 선택</option>
                    <option value="SMS">SMS (90바이트 이하)</option>
                    <option value="LMS">LMS (장문)</option>
                  </Select>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    자동 선택 권장
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold" color={textColor}>
                    제목 (LMS용)
                  </FormLabel>
                  <Input
                    placeholder="알림"
                    value={smsForm.subject}
                    onChange={(e) =>
                      setSmsForm({ ...smsForm, subject: e.target.value })
                    }
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    LMS일 경우에만 사용
                  </Text>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold" color={textColor}>
                    메시지 내용
                  </FormLabel>
                  <Textarea
                    placeholder="보낼 메시지를 입력하세요"
                    value={smsForm.content}
                    onChange={(e) =>
                      setSmsForm({ ...smsForm, content: e.target.value })
                    }
                    rows={8}
                  />
                  <HStack justify="space-between" mt={2}>
                    <Text fontSize="xs" color="gray.500">
                      {contentLength}자 / {contentBytes}바이트
                    </Text>
                    <Badge colorScheme={contentBytes <= 90 ? "green" : "blue"}>
                      예상: {estimatedType}
                    </Badge>
                  </HStack>
                </FormControl>

                <Divider />

                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box fontSize="sm">
                    <Text fontWeight="bold">발신번호 정보</Text>
                    <Text>발신번호: {FROM_NUMBER}</Text>
                  </Box>
                </Alert>

                <Button
                  colorScheme="purple"
                  size="lg"
                  onClick={handleSendSMS}
                  isLoading={loading}
                  loadingText="발송 중..."
                  isDisabled={recipients.length === 0}
                >
                  전체 {recipients.length}명 발송
                </Button>
              </VStack>
            </CardBody>
          </Card>
        </Box>

        {/* 우측: 수신자 관리 */}
        <Box>
          <Card>
            <CardHeader mb="20px">
              <HStack justify="space-between">
                <Text fontSize="lg" color={textColor} fontWeight="bold">
                  받는사람
                </Text>
                <Badge colorScheme="blue" fontSize="md">
                  {recipients.length}명
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {/* 수신자 입력 */}
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color={textColor} mb={2}>
                    직접입력
                  </Text>
                  <FormControl mb={2}>
                    <Input
                      placeholder="전화번호 (01012345678)"
                      value={tempRecipient.phoneNumber}
                      onChange={(e) =>
                        setTempRecipient({
                          ...tempRecipient,
                          phoneNumber: e.target.value,
                        })
                      }
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleAddRecipient();
                        }
                      }}
                    />
                  </FormControl>
                  <FormControl mb={2}>
                    <Input
                      placeholder="이름 (선택)"
                      value={tempRecipient.name}
                      onChange={(e) =>
                        setTempRecipient({
                          ...tempRecipient,
                          name: e.target.value,
                        })
                      }
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleAddRecipient();
                        }
                      }}
                    />
                  </FormControl>
                  <Button
                    colorScheme="teal"
                    size="sm"
                    width="100%"
                    onClick={handleAddRecipient}
                  >
                    추가
                  </Button>
                </Box>

                <Divider />

                {/* 수신자 목록 */}
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      발송 대상
                    </Text>
                    {recipients.length > 0 && (
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        onClick={handleClearRecipients}
                      >
                        전체삭제
                      </Button>
                    )}
                  </HStack>

                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    maxH="400px"
                    overflowY="auto"
                    p={2}
                  >
                    {recipients.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                        수신자를 추가해주세요
                      </Text>
                    ) : (
                      <VStack spacing={2} align="stretch">
                        {recipients.map((recipient, index) => (
                          <Flex
                            key={recipient.id}
                            justify="space-between"
                            align="center"
                            p={2}
                            borderWidth="1px"
                            borderRadius="md"
                            bg={useColorModeValue("gray.50", "gray.700")}
                          >
                            <Box flex="1">
                              <HStack>
                                <Badge colorScheme="gray">{index + 1}</Badge>
                                <Box>
                                  <Text fontSize="sm" fontWeight="bold">
                                    {recipient.name}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {recipient.phoneNumber}
                                  </Text>
                                </Box>
                              </HStack>
                            </Box>
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleRemoveRecipient(recipient.id)}
                            >
                              삭제
                            </Button>
                          </Flex>
                        ))}
                      </VStack>
                    )}
                  </Box>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        </Box>

        {/* 응답 결과 */}
        {response && (
          <Box gridColumn={{ sm: "1", lg: "1 / 3" }}>
            <Card>
              <CardHeader mb="20px">
                <HStack>
                  <Text fontSize="lg" color={textColor} fontWeight="bold">
                    발송 결과
                  </Text>
                  <Badge colorScheme={response.success ? "green" : "red"}>
                    {response.success ? "완료" : "실패"}
                  </Badge>
                  {response.totalCount && (
                    <>
                      <Badge colorScheme="blue">
                        전체 {response.totalCount}명
                      </Badge>
                      <Badge colorScheme="green">
                        성공 {response.successCount}명
                      </Badge>
                      {response.failCount > 0 && (
                        <Badge colorScheme="red">
                          실패 {response.failCount}명
                        </Badge>
                      )}
                    </>
                  )}
                </HStack>
              </CardHeader>
              <CardBody>
                {response.results ? (
                  <Box maxH="300px" overflowY="auto">
                    <VStack spacing={2} align="stretch">
                      {response.results.map((result, index) => (
                        <Flex
                          key={index}
                          justify="space-between"
                          align="center"
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          bg={
                            result.success
                              ? useColorModeValue("green.50", "green.900")
                              : useColorModeValue("red.50", "red.900")
                          }
                        >
                          <Box>
                            <Text fontSize="sm" fontWeight="bold">
                              {result.recipient}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {result.phoneNumber}
                            </Text>
                            {result.error && (
                              <Text fontSize="xs" color="red.500" mt={1}>
                                오류: {result.error}
                              </Text>
                            )}
                          </Box>
                          <Badge
                            colorScheme={result.success ? "green" : "red"}
                          >
                            {result.success ? "성공" : "실패"}
                          </Badge>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                ) : (
                  <Code
                    display="block"
                    whiteSpace="pre"
                    p={4}
                    borderRadius="md"
                    bg={bgColor}
                    overflowX="auto"
                  >
                    {JSON.stringify(response, null, 2)}
                  </Code>
                )}
              </CardBody>
            </Card>
          </Box>
        )}

        {/* 사용 안내 */}
        <Box gridColumn={{ sm: "1", lg: "1 / 3" }}>
          <Card>
            <CardHeader mb="20px">
              <Text fontSize="lg" color={textColor} fontWeight="bold">
                사용 가이드
              </Text>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color={textColor} mb={2}>
                    📌 메시지 타입
                  </Text>
                  <VStack align="stretch" spacing={2} pl={4}>
                    <Text fontSize="sm" color="gray.600">
                      • <strong>SMS</strong>: 90바이트 이하 (한글 45자 내외)
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      • <strong>LMS</strong>: 90바이트 초과 ~ 2000바이트 (한글 1000자 내외)
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      • <strong>자동 선택</strong>: 내용 길이에 따라 자동으로 SMS/LMS 결정 (권장)
                    </Text>
                  </VStack>
                </Box>

                <Divider />

                <Box>
                  <Text fontSize="sm" fontWeight="bold" color={textColor} mb={2}>
                    📌 주의사항
                  </Text>
                  <VStack align="stretch" spacing={2} pl={4}>
                    <Text fontSize="sm" color="gray.600">
                      • 발신번호는 사전 등록된 번호만 사용 가능합니다
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      • 수신번호는 하이픈 없이 숫자만 입력해주세요
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      • LMS 발송 시 제목이 함께 전송됩니다
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      • 특수문자는 바이트 수가 더 크므로 주의하세요
                    </Text>
                  </VStack>
                </Box>

                <Divider />
              </VStack>
            </CardBody>
          </Card>
        </Box>
      </Grid>
    </Flex>
  );
}

export default SMSManagement;

